import type { VisibleNavigationEntry } from '@Guidelines/Shell/AppShell/Hooks/useVisibleNavigationMenu';
import navChipStyle from '@Guidelines/Shell/AppShell/AppHeader/PrimaryNav/Shared/navChipStyle';
import { css } from '@emotion/react';
import { ChevronDown } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu } from '@verclam/ui-kit';
import type { MenuHandle, MenuItem } from '@verclam/ui-kit';

/** A button carrying a chevron: reset the native button look and space the chevron from the label. */
const buttonStyle = css`
	gap: 0.25rem;
	border: none;
	cursor: pointer;
`;

type Props = {
	readonly items: readonly VisibleNavigationEntry[];
	readonly isActive: boolean;
};

/** One « Dossiers » chip opening a popup menu of the dossier types, in menu order. */
const PrimaryNavSubjectsMenu: React.FC<Props> = ({ items, isActive }) => {
	const navigate = useNavigate();
	const menuRef = React.useRef<MenuHandle>(null);
	const [isOpen, setIsOpen] = React.useState(false);

	const handleToggle = React.useCallback((event: React.SyntheticEvent) => {
		menuRef.current?.toggle(event);
	}, []);

	const handleShow = React.useCallback(() => {
		setIsOpen(true);
	}, []);

	const handleHide = React.useCallback(() => {
		setIsOpen(false);
	}, []);

	const model = React.useMemo<readonly MenuItem[]>(() => {
		return items.map((item) => {
			const Icon = item.entry.icon;

			return {
				id: item.entry.id,
				label: item.entry.label,
				icon: <Icon size={16} />,
				command: () => navigate(item.href),
			};
		});
	}, [items, navigate]);

	return (
		<React.Fragment>
			<button
				type="button"
				css={[navChipStyle.chip, buttonStyle, isActive ? navChipStyle.active : null]}
				onClick={handleToggle}
				aria-haspopup="menu"
				aria-expanded={isOpen}
			>
				Dossiers
				<ChevronDown size={16} />
			</button>
			<Menu
				ref={menuRef}
				popup
				model={model}
				ariaLabel="Types de dossier"
				onShow={handleShow}
				onHide={handleHide}
			/>
		</React.Fragment>
	);
};

export default PrimaryNavSubjectsMenu;
